import type { SyntheticEvent } from "react";
import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { docUrl } from "../lib/docLinks";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import {
  Button,
  Copyable,
  EmptyState,
  Grid,
  GridItem,
  HelpTip,
  Inline,
  InlineError,
  KeyValue,
  Page,
  Section,
  Skeleton,
  Spinner,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../ui";

type Organization = components["schemas"]["OrganizationResponse"];
type RetentionPatch = components["schemas"]["RetentionPolicyPatch"];
type RetentionPolicy = components["schemas"]["RetentionPolicyResponse"];

// Every clause below is verified against the code that enforces it: only
// `trace_retention_days` is read by a delete path (queue maintenance ->
// TraceStore.purge_expired, `packages/core/.../maintenance.py`), object expiry
// comes from the object-store lifecycle rules in `infra/terraform`, and no code
// path deletes transcript or answer rows. Do not widen these claims.
const retentionHelp =
  "Only trace retention is enforced here: maintenance purges trace spans. " +
  "Audio expiry comes from deployment lifecycle rules. Transcript and answer " +
  "purges are not built, and there is no erase-now action.";

const retentionWindowHelp =
  "Minimum 30 days. Leave it blank to keep this data indefinitely.";

const retentionFields = [
  {
    key: "transcript_retention_days",
    label: "Transcript retention (days)",
    readOnlyLabel: "Transcript",
  },
  {
    key: "audio_retention_days",
    label: "Audio retention (days)",
    readOnlyLabel: "Audio",
  },
  {
    key: "trace_retention_days",
    label: "Trace retention (days)",
    readOnlyLabel: "Trace",
  },
  {
    key: "answer_retention_days",
    label: "Answer retention (days)",
    readOnlyLabel: "Answer",
  },
] as const satisfies ReadonlyArray<{
  key: keyof RetentionPatch;
  label: string;
  readOnlyLabel: string;
}>;

type RetentionFieldKey = (typeof retentionFields)[number]["key"];
type RetentionDraft = Record<RetentionFieldKey, string>;

const emptyDraft: RetentionDraft = {
  answer_retention_days: "",
  audio_retention_days: "",
  trace_retention_days: "",
  transcript_retention_days: "",
};

function draftFromPolicy(policy: RetentionPolicy): RetentionDraft {
  return {
    answer_retention_days:
      policy.answer_retention_days == null
        ? ""
        : String(policy.answer_retention_days),
    audio_retention_days:
      policy.audio_retention_days == null
        ? ""
        : String(policy.audio_retention_days),
    trace_retention_days:
      policy.trace_retention_days == null
        ? ""
        : String(policy.trace_retention_days),
    transcript_retention_days:
      policy.transcript_retention_days == null
        ? ""
        : String(policy.transcript_retention_days),
  };
}

function parseDraftValue(value: string): number | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : Number(trimmed);
}

function patchFromDraft(draft: RetentionDraft): RetentionPatch {
  return {
    answer_retention_days: parseDraftValue(draft.answer_retention_days),
    audio_retention_days: parseDraftValue(draft.audio_retention_days),
    trace_retention_days: parseDraftValue(draft.trace_retention_days),
    transcript_retention_days: parseDraftValue(draft.transcript_retention_days),
  };
}

function draftMatchesPolicy(
  draft: RetentionDraft,
  policy: RetentionPolicy,
): boolean {
  const policyDraft = draftFromPolicy(policy);
  return retentionFields.every(
    (field) => draft[field.key] === policyDraft[field.key],
  );
}

function formatRetentionWindow(value: number | null): string {
  if (value == null) {
    return "— (keep indefinitely)";
  }

  return value === 1 ? "1 day" : `${value.toLocaleString()} days`;
}

function firstErrorMessage(
  retentionError: Error | null,
  meError: Error | null,
  organizationError: Error | null,
): string | null {
  return (
    retentionError?.message ??
    meError?.message ??
    organizationError?.message ??
    null
  );
}

async function getMe() {
  const { data, error } = await apiClient.GET("/v1/me");
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

async function getOrganization(id: string): Promise<Organization> {
  const { data, error } = await apiClient.GET("/v1/organizations/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

async function listRetentionPolicies() {
  const { data, error } = await apiClient.GET("/v1/retention-policies", {
    params: { query: { page_size: 1 } },
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

export function RetentionSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<RetentionDraft>(emptyDraft);
  const [loadedPolicyId, setLoadedPolicyId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: getMe,
  });

  const organizationId = me.data?.organization.id;
  const organization = useQuery({
    enabled: Boolean(organizationId),
    queryKey: queryKeys.organizations.detail(organizationId ?? "pending"),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization id is required.");
      }
      return getOrganization(organizationId);
    },
  });

  const retentionPolicies = useQuery({
    queryKey: queryKeys.retentionPolicies.list(),
    queryFn: listRetentionPolicies,
  });

  const patchRetentionPolicy = useMutation({
    mutationFn: async ({ body, id }: { body: RetentionPatch; id: string }) => {
      const { data, error } = await apiClient.PATCH(
        "/v1/retention-policies/{id}",
        {
          body,
          params: { path: { id } },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (policy) => {
      setDraft(draftFromPolicy(policy));
      setSaved(true);
      toast.success("Retention updated");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.retentionPolicies.all,
      });
    },
    onError: (error) => {
      toast.error("Retention update failed", {
        description: requestErrorDescription(
          error,
          "Retention request failed.",
        ),
      });
    },
  });

  const policy = retentionPolicies.data?.items[0] ?? null;
  const canEdit =
    me.data?.user.id != null &&
    me.data.user.id === organization.data?.owner_app_user_id;
  const isPending =
    retentionPolicies.isPending ||
    me.isPending ||
    (Boolean(organizationId) && organization.isPending);
  const errorMessage = firstErrorMessage(
    retentionPolicies.isError ? retentionPolicies.error : null,
    me.isError ? me.error : null,
    organization.isError ? organization.error : null,
  );

  if (policy && policy.id !== loadedPolicyId) {
    setDraft(draftFromPolicy(policy));
    setLoadedPolicyId(policy.id);
    setSaved(false);
  }

  function updateDraft(key: RetentionFieldKey, value: string): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  function submitDraft(): void {
    if (!policy) {
      return;
    }

    setSaved(false);
    patchRetentionPolicy.mutate({
      body: patchFromDraft(draft),
      id: policy.id,
    });
  }

  return (
    <RetentionSettingsView
      canEdit={canEdit}
      draft={draft}
      errorMessage={errorMessage}
      isPending={isPending}
      isSavePending={patchRetentionPolicy.isPending}
      onDraftChange={updateDraft}
      onSubmit={submitDraft}
      policy={policy}
      saved={saved}
      saveErrorMessage={
        patchRetentionPolicy.isError
          ? patchRetentionPolicy.error.message
          : undefined
      }
    />
  );
}

export interface RetentionSettingsViewProps {
  canEdit: boolean;
  draft: RetentionDraft;
  errorMessage: string | null;
  isPending: boolean;
  isSavePending: boolean;
  onDraftChange: (key: RetentionFieldKey, value: string) => void;
  onSubmit: () => void;
  policy: RetentionPolicy | null;
  saved: boolean;
  saveErrorMessage?: string;
}

export function RetentionSettingsView({
  canEdit,
  draft,
  errorMessage,
  isPending,
  isSavePending,
  onDraftChange,
  onSubmit,
  policy,
  saved,
  saveErrorMessage,
}: RetentionSettingsViewProps) {
  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!policy) {
      return;
    }

    onSubmit();
  }

  return (
    <Page variant="config">
      {isPending ? (
        <Skeleton
          label="Loading retention settings"
          rows={4}
          shape="detail-field"
        />
      ) : null}
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {!isPending && !errorMessage && policy == null ? (
        <EmptyState title="No retention policy for this organization" />
      ) : null}

      {!isPending && !errorMessage && policy ? (
        <Stack gap={6}>
          <Section
            help={
              <HelpTip
                docHref={docUrl("account/retention-and-audit")}
                docLabel="Retention and audit"
                label="About retention windows"
              >
                {retentionHelp}
              </HelpTip>
            }
          >
            {canEdit ? (
              <form onSubmit={handleSubmit}>
                <Grid columns={2} gap={4}>
                  {retentionFields.map((field) => (
                    <TextInput
                      help={
                        <HelpTip
                          label={`About ${field.readOnlyLabel} retention`}
                        >
                          {retentionWindowHelp}
                        </HelpTip>
                      }
                      key={field.key}
                      label={field.label}
                      min={30}
                      name={field.key}
                      type="number"
                      value={draft[field.key]}
                      onChange={(event) => {
                        onDraftChange(field.key, event.currentTarget.value);
                      }}
                    />
                  ))}
                  <GridItem span={2}>
                    {saveErrorMessage ? (
                      <InlineError>{saveErrorMessage}</InlineError>
                    ) : null}
                    {isSavePending ? (
                      <Inline gap={2}>
                        <Spinner label="Saving retention settings" />
                        <Text variant="muted">Saving...</Text>
                      </Inline>
                    ) : null}
                    {saved && !isSavePending ? (
                      <Text role="status">Saved.</Text>
                    ) : null}
                  </GridItem>
                  <GridItem span={2}>
                    <Inline gap={3}>
                      <Button
                        disabled={
                          isSavePending || draftMatchesPolicy(draft, policy)
                        }
                        type="submit"
                      >
                        {isSavePending ? "Saving..." : "Save"}
                      </Button>
                    </Inline>
                  </GridItem>
                </Grid>
              </form>
            ) : (
              <Stack gap={2}>
                <KeyValue
                  rows={retentionFields.map((field) => ({
                    label: field.readOnlyLabel,
                    value: formatRetentionWindow(policy[field.key]),
                  }))}
                />
                <Text variant="muted">
                  Only the organization owner can edit retention windows.
                </Text>
              </Stack>
            )}
          </Section>

          <Section>
            <KeyValue
              rows={[
                {
                  label: "Policy ID",
                  value: <Copyable label="Policy ID" value={policy.id} />,
                },
                { label: "Created", value: formatDateTime(policy.created_at) },
                { label: "Updated", value: formatDateTime(policy.updated_at) },
              ]}
            />
          </Section>
        </Stack>
      ) : null}
    </Page>
  );
}
